import { MAX_FILE_SIZE } from '../constants/FileConstants';
import { performFetch } from './FetchUtils';
import Logger from './Logger';

export const isDocSizeValid = (file: any): boolean => {
  if (file.size! > MAX_FILE_SIZE) {
    return false;
  }

  return true;
};

export const bytesToSize = (bytes: number) => {
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
  if (bytes === 0) {
    return 'N/A';
  }

  const index = Math.min(
    Math.floor(Math.log(bytes) / Math.log(1024)),
    sizes?.length - 1,
  );

  if (index === 0) {
    return `${bytes} ${sizes[index]}`;
  }

  return `${(bytes / 1024 ** index)?.toFixed(0)} ${sizes[index]}`;
};

export const isDocSizeInvalid = (file: File, maxDocSize: number): boolean => {
  return file?.size > maxDocSize;
};

export const isDocsSizeInvalid = (
  files: File[] = [],
  maxDocSize: number,
): boolean => {
  return !!files?.length && files?.some((file) => file?.size > maxDocSize);
};

export const validDocs = (files: File[] = [], maxDocSize: number) => {
  const validFiles = files?.filter((file) => file?.size <= maxDocSize);

  return validFiles;
};

export const convertMegaBytesToBytes = (mb: number) => mb * 1024 * 1024;

export const getFileBlob = async (
  fileUrl: string,
  headers?: RequestInit,
): Promise<Blob | undefined> => {
  try {
    const fetchedFile = await performFetch(fileUrl, headers);
    const fileBlob = await fetchedFile.blob();
    return fileBlob;
  } catch (e) {
    return undefined;
  }
};

export const copyImageToClipboard = async (imgUrl: string) => {
  try {
    const img = new Image();
    img.crossOrigin = 'Anonymous';
    img.onload = async () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.width;
      canvas.height = img.height;

      const ctx = canvas.getContext('2d');
      ctx!.drawImage(img, 0, 0);

      // Convert the image to PNG format
      const pngDataUrl = canvas.toDataURL('image/png');
      const imgblob = await getFileBlob(pngDataUrl);
      await navigator.clipboard.write([
        new ClipboardItem({
          [imgblob!.type]: imgblob!,
        }),
      ]);
    };
    img.src = imgUrl;
  } catch (error: any) {
    Logger.error('Error copying image to clipboard', error);
  }
};

export const downloadFile = async (
  imageSrc: string,
  fileName: string = 'fileName',
  headers?: RequestInit,
) => {
  const imageBlob = await getFileBlob(imageSrc, headers);
  const imageURL = URL.createObjectURL(imageBlob!);

  const link = document.createElement('a');
  link.href = imageURL;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

export const isPDFPasswordProtected = (file: File): Promise<boolean> => {
  return new Promise((resolve) => {
    // Check if file is actually a PDF
    if (file.type !== 'application/pdf') {
      resolve(false);
      return;
    }

    const reader = new FileReader();

    reader.onload = (e) => {
      try {
        const content = e.target?.result as string;
        // Check for encryption marker in PDF structure
        // PDF encryption is indicated by the /Encrypt dictionary
        const isProtected = content.includes('/Encrypt');
        resolve(isProtected);
      } catch (error) {
        resolve(false);
      }
    };

    reader.onerror = () => {
      resolve(false);
    };

    // Read file as text to check for encryption markers
    reader.readAsText(file);
  });
};

export const isValidPDF = (file: File): Promise<boolean> => {
  return new Promise((resolve) => {
    // Check if file is actually a PDF
    if (file.type !== 'application/pdf') {
      resolve(true); // Not a PDF, so considered valid for PDF check
      return;
    }

    const reader = new FileReader();

    reader.onload = (e) => {
      try {
        const content = e.target?.result as string;
        // Check for PDF header - all valid PDFs start with %PDF-
        const hasPDFHeader = content.startsWith('%PDF-');
        // Check for PDF trailer - all valid PDFs should have %%EOF
        const hasPDFTrailer = content.includes('%%EOF');

        // A valid PDF must have both header and trailer
        const isValid = hasPDFHeader && hasPDFTrailer;
        resolve(isValid);
      } catch (error) {
        // If we can't read it, it's likely corrupt
        resolve(false);
      }
    };

    reader.onerror = () => {
      // File read error means it's corrupt
      resolve(false);
    };

    // Read file as text to check for basic PDF structure
    reader.readAsText(file);
  });
};

export const validatePDFFile = async (
  file: File,
): Promise<{ isValid: boolean; errorMessage?: string }> => {
  // Only validate if it's a PDF file
  if (file.type !== 'application/pdf') {
    return { isValid: true }; // Not a PDF, no PDF-specific validation needed
  }

  // Check if password protected
  const isProtected = await isPDFPasswordProtected(file);
  if (isProtected) {
    return {
      isValid: false,
      errorMessage: 'Password-protected PDFs are not supported.',
    };
  }

  // Check if corrupt or invalid
  const isValidFile = await isValidPDF(file);
  if (!isValidFile) {
    return {
      isValid: false,
      errorMessage:
        'The PDF file appears to be corrupt or invalid. Please try a different file.',
    };
  }

  return { isValid: true };
};

export const getFileNameFromURL = (url: string) => {
  const file = url?.match(/^[^?]*/);
  const fileName = file?.[0]?.replace(/^(https?:\/\/).*(.com\/)/, '');

  return fileName;
};
